import React, { useEffect, useState } from 'react';
import { Table, Loader, Message, Pagination, Modal } from 'semantic-ui-react';
import { getBids } from '~/api/admin';
import BidRow from './BidRow';
import BidIrrForm from '../projectBidding/shared/BidIrrForm';

interface BidTableProps {
    bidFilters: any;
    currentPage: number;
    onPageChange: (page: number) => void;
    hasFilters: boolean;
    activeFilterDescription: string | null;
    onClearFilters: () => void;
}

interface BidData {
    id: string;
    hashId: string;
    projectId: string;
    amount: number;
    unit: string;
    bidStatus: string;
    createdAt: string;
    calculatedIrr: number | null;
    manualIrr?: number | null;
    irrSpreadsheetUrl?: string;
    manualIrrSpreadsheetUrl?: string | null;
    project?: {
        id: string;
        hashId: string;
        name: string;
        portfolioId: string | null;
        portfolio?: {
            id: string;
            hashId: string;
            name: string;
        };
    };
    investor?: {
        id: string;
        company?: {
            id: string;
            name: string;
        };
    };
}

export default function BidTable({
    bidFilters,
    currentPage,
    onPageChange,
    hasFilters,
    activeFilterDescription,
    onClearFilters,
}: BidTableProps) {
    const [bids, setBids] = useState<BidData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalFromApi, setTotalFromApi] = useState(0);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [editingBidId, setEditingBidId] = useState<string | null>(null);

    const PAGE_SIZE = 50;

    useEffect(() => {
        const fetchBids = async () => {
            try {
                setLoading(true);
                setError(null);

                const offset = (currentPage - 1) * PAGE_SIZE;
                const params: any = {
                    limit: PAGE_SIZE,
                    offset
                };

                // Add filters to params
                if (bidFilters.selectedStatuses && bidFilters.selectedStatuses.length > 0) {
                    params.selectedStatuses = bidFilters.selectedStatuses;
                }

                if (bidFilters.amountMin) {
                    params.amountMin = parseFloat(bidFilters.amountMin);
                }

                if (bidFilters.amountMax) {
                    params.amountMax = parseFloat(bidFilters.amountMax);
                }

                if (bidFilters.selectedUnits && bidFilters.selectedUnits.length > 0) {
                    params.selectedUnits = bidFilters.selectedUnits;
                }

                if (bidFilters.selectedDeploymentType) {
                    params.selectedDeploymentType = bidFilters.selectedDeploymentType;
                }

                if (bidFilters.projectName) {
                    params.projectName = bidFilters.projectName;
                }

                if (bidFilters.portfolioName) {
                    params.portfolioName = bidFilters.portfolioName;
                }

                if (bidFilters.investorCompanyName) {
                    params.investorCompanyName = bidFilters.investorCompanyName;
                }

                if (bidFilters.state) {
                    params.addressState = bidFilters.state;
                }

                if (bidFilters.dateFrom) {
                    params.dateFrom = bidFilters.dateFrom;
                }

                if (bidFilters.dateTo) {
                    params.dateTo = bidFilters.dateTo;
                }

                if (sortColumn) {
                    params.sortBy = sortColumn;
                    params.sortDirection = sortDirection;
                }

                const response = await getBids(params);

                // Extract bids and total from response
                const responseBids = response.bids || response;
                const apiTotal = response.total || (Array.isArray(response) ? response.length : 0);
                setTotalFromApi(apiTotal);

                // Use filtered bids from backend
                let filteredBids = Array.isArray(responseBids) ? responseBids : [responseBids];

                setBids(filteredBids);
            } catch (err: any) {
                setError(err?.message || 'Failed to fetch bids');
            } finally {
                setLoading(false);
            }
        };

        fetchBids();
    }, [bidFilters, currentPage, sortColumn, sortDirection]);

    const handleOpenEdit = (bidId: string, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setEditingBidId(bidId);
    };

    const handleCloseEdit = () => {
        setEditingBidId(null);
    };

    const handleBidUpdate = (bidId: string, updatedData: any) => {
        setBids(prev => prev.map(bid =>
            bid.id === bidId ? { ...bid, ...updatedData } : bid
        ));
        handleCloseEdit();
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    const getSortIcon = (column: string) => {
        if (sortColumn !== column) return null;
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    if (error) {
        return <Message negative>{error}</Message>;
    }

    const totalPages = Math.ceil(totalFromApi / PAGE_SIZE);

    return (
        <div>
            {/* IRR Edit Modal */}
            <Modal
                open={!!editingBidId}
                onClose={handleCloseEdit}
                size="tiny"
            >
                <Modal.Header>Edit IRR</Modal.Header>
                <Modal.Content>
                    {editingBidId && bids.find(b => b.id === editingBidId) && (
                        <BidIrrForm
                            bid={{
                                id: bids.find(b => b.id === editingBidId)!.id,
                                calculatedIrr: bids.find(b => b.id === editingBidId)!.calculatedIrr ?? undefined,
                                manualIrr: bids.find(b => b.id === editingBidId)!.manualIrr ?? undefined,
                                irrSpreadsheetUrl: bids.find(b => b.id === editingBidId)!.irrSpreadsheetUrl,
                                manualIrrSpreadsheetUrl: bids.find(b => b.id === editingBidId)!.manualIrrSpreadsheetUrl ?? undefined
                            }}
                            projectId={bids.find(b => b.id === editingBidId)?.projectId || ''}
                            onSave={(updatedBid) => handleBidUpdate(editingBidId, updatedBid)}
                            onCancel={handleCloseEdit}
                        />
                    )}
                </Modal.Content>
            </Modal>

            {loading && <Loader active inline="centered" />}
            {!loading && bids.length === 0 && (
                <Message info>No bids found matching your filters.</Message>
            )}
            {!loading && bids.length > 0 && (
                <>
                    <div className='admin-companies-search-meta'>
                        Showing {bids.length > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0} - {Math.min(currentPage * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + bids.length)} of {totalFromApi} bids
                        {hasFilters && (
                            <>
                                {' - '}
                                <button
                                    type='button'
                                    className='admin-companies-clear-link'
                                    onClick={onClearFilters}
                                >
                                    Clear filters
                                </button>
                            </>
                        )}
                        {activeFilterDescription && (
                            <>
                                <br />
                                {activeFilterDescription}
                            </>
                        )}
                    </div>
                    <Table striped>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell
                                    onClick={() => handleSort('projectName')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Project Name{getSortIcon('projectName')}
                                </Table.HeaderCell>
                                <Table.HeaderCell>Portfolio Name</Table.HeaderCell>
                                <Table.HeaderCell>Investor</Table.HeaderCell>
                                <Table.HeaderCell
                                    onClick={() => handleSort('amount')}
                                    style={{ cursor: 'pointer', textAlign: 'right' }}
                                >
                                    Amount{getSortIcon('amount')}
                                </Table.HeaderCell>
                                <Table.HeaderCell>Unit</Table.HeaderCell>
                                <Table.HeaderCell
                                    onClick={() => handleSort('irr')}
                                    style={{ cursor: 'pointer', textAlign: 'right' }}
                                >
                                    IRR{getSortIcon('irr')}
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{ textAlign: 'center'}}>Status</Table.HeaderCell>
                                <Table.HeaderCell
                                    onClick={() => handleSort('createdAt')}
                                    style={{ cursor: 'pointer', textAlign: 'right' }}
                                >
                                    Created {getSortIcon('createdAt')}
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {bids.map((bid) => (
                                <BidRow key={bid.hashId} bid={bid} onEditIrr={handleOpenEdit} />
                            ))}
                        </Table.Body>
                    </Table>

                    <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {totalPages > 1 && (
                            <Pagination
                                activePage={currentPage}
                                onPageChange={(e, { activePage }) => onPageChange(activePage as number)}
                                totalPages={totalPages}
                                siblingRange={1}
                                boundaryRange={1}
                                ellipsisItem={null}
                                firstItem={null}
                                lastItem={null}
                                prevItem={{ content: '←', icon: false }}
                                nextItem={{ content: '→', icon: false }}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
